// Persists access + refresh tokens and the authenticated user in localStorage.
// `subscribe(fn)` is called whenever auth state changes so views can react.

const STORAGE_KEY = 'nestly.auth.v1';
const MANAGE_PROPERTY_ROLES = ['LANDLORD', 'AGENT', 'AGENCY_ADMIN'];

class AuthStore {
  constructor() {
    this._listeners = new Set();
    this._state = this._load();
  }

  get state() { return this._state; }
  get accessToken() { return this._state.accessToken; }
  get refreshToken() { return this._state.refreshToken; }
  get user() { return this._state.user; }
  get isAuthenticated() { return !!this._state.accessToken; }

  get availableRoles() {
    return (this._state.user && this._state.user.roles) || [];
  }

  // The role the user is currently acting as. May be null when login is fresh
  // and the user must still pick one (multi-role accounts).
  get activeRole() {
    return this._state.activeRole || null;
  }

  // True when the account has any role at all (always true for authed users).
  get needsRoleChoice() {
    return this.isAuthenticated && this.availableRoles.length > 1 && !this.activeRole;
  }

  hasAnyRole(...roles) {
    return roles.some((r) => this.availableRoles.includes(r));
  }

  get canManageProperties() {
    if (!this.isAuthenticated) return false;
    if (this.activeRole) return MANAGE_PROPERTY_ROLES.includes(this.activeRole);
    return this.hasAnyRole(...MANAGE_PROPERTY_ROLES);
  }

  get marketplaceEnabled() {
    return !!(this._state.user && this._state.user.marketplace_enabled);
  }

  patchUser(patch) {
    if (!this._state.user) return;
    this._state = { ...this._state, user: { ...this._state.user, ...patch } };
    this._persist();
    this._emit();
  }

  setActiveRole(role) {
    if (role && !this.availableRoles.includes(role)) return;
    this._state = { ...this._state, activeRole: role || null };
    this._persist();
    this._emit();
  }

  defaultPath() {
    if (!this.isAuthenticated) return '/login';
    if (this.needsRoleChoice) return '/select-role';
    return this.canManageProperties ? '/properties' : '/contracts';
  }

  set(payload) {
    const roles = (payload.user && payload.user.roles) || [];
    this._state = {
      accessToken: payload.access_token || payload.accessToken || null,
      refreshToken: payload.refresh_token || payload.refreshToken || null,
      user: payload.user || null,
      // Auto-pick when there is exactly one role; otherwise leave null and let
      // the role chooser screen set it.
      activeRole: roles.length === 1 ? roles[0] : null,
    };
    this._persist();
    this._emit();
  }

  setTokens({ access_token, refresh_token }) {
    this._state = { ...this._state, accessToken: access_token, refreshToken: refresh_token };
    this._persist();
    this._emit();
  }

  clear() {
    this._state = { accessToken: null, refreshToken: null, user: null, activeRole: null };
    this._persist();
    this._emit();
  }

  subscribe(fn) {
    this._listeners.add(fn);
    return () => this._listeners.delete(fn);
  }

  _load() {
    const empty = { accessToken: null, refreshToken: null, user: null, activeRole: null };
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return empty;
      const parsed = JSON.parse(raw);
      // Back-fill missing fields for sessions stored before activeRole existed.
      return { activeRole: null, ...parsed };
    } catch (_) {
      return empty;
    }
  }

  _persist() {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(this._state)); } catch (_) {}
  }

  _emit() { this._listeners.forEach((fn) => fn(this._state)); }
}

export const auth = new AuthStore();
